import React from "react";
import { ArrowRight, House, Building2, Factory } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const solutions = [{
  id: "residential",
  title: "Residential Painting",
  icon: House,
  description: "Premium interior and exterior finishes for homes across the Central Coast. We use high-quality, low-VOC paints.",
  specs: ["Interior & Exterior", "30+ Years Experience"],
  link: "/ServicesPage"
}, {
  id: "interior-exterior",
  title: "Interior & Exterior",
  icon: Building2,
  description: "Specialised coatings designed to withstand the salt air and coastal sun. Detailed preparation for a lasting finish.",
  specs: ["Weather Protection", "Detailed Sanding"],
  link: "/ServicesPage"
}, {
  id: "industrial",
  title: "Commercial",
  icon: Factory,
  description: "Large-scale painting solutions for commercial and retail spaces. Reliable scheduling to minimise business disruption.",
  specs: ["Safety Compliant", "Large-Scale Ready"],
  link: "/ServicesPage"
}];
export default function OurSpecializedSolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="29722" id="comprehensive-painting-solutions" ref={ref} className="relative w-full py-24 bg-background overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-chart-2/5 rounded-full blur-3xl opacity-40" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4 tracking-tight">
            Comprehensive Painting Solutions
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-xl leading-relaxed">Our high standards of sanding and priming ensure a perfect finish. Professional painting tailored for Central Coast homes and facilities.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {solutions.map((solution, i) => <div key={solution.id} data-index={i} className={`transition-all duration-700 delay-${i * 150} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              <Link to={solution.link} className="block h-full group">
                <Card className="h-full border-border bg-card/50 backdrop-blur-sm transition-all duration-500 hover:shadow-2xl hover:shadow-chart-2/20 hover:border-chart-2/40 hover:bg-linear-to-br hover:from-background hover:to-chart-2/5 group-hover:-translate-y-1 overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="mb-6 inline-flex p-4 rounded-xl bg-chart-2/10 border border-chart-2/20 text-chart-2 shadow-sm backdrop-blur-md group-hover:bg-chart-2/20 group-hover:scale-110 transition-all duration-500">
                      <solution.icon className="w-8 h-8" strokeWidth={1.5} />
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs font-mono text-chart-2 uppercase tracking-widest font-semibold opacity-90">
                        Expert Service
                      </span>
                      <CardTitle className="text-2xl font-bold text-primary group-hover:text-chart-2 transition-colors">
                        {solution.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-6">
                    <CardDescription className="text-base leading-relaxed text-muted-foreground !font-sans">
                      {solution.description}
                    </CardDescription>
                    
                    <div className="space-y-3 pt-2">
                      {solution.specs.map((spec, idx) => <div key={idx} className="flex items-center text-sm text-muted-foreground font-mono">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent mr-3 shadow-[0_0_8px_var(--color-accent)/50]" />
                          {spec}
                        </div>)}
                    </div>
                  </CardContent>

                  <CardFooter className="pt-4">
                    <Button variant="outline" className="w-full justify-between border-border text-primary font-semibold tracking-wide group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all duration-300 rounded-lg">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </CardFooter>
                </Card>
              </Link>
            </div>)}
        </div>
      </div>
    </section>;
}